import React, { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";

import api from "/imports/api/client";
import { NO_CAPTURE } from "/imports/libs/analytics";
import { formatTime } from "/imports/ui/views/TrainerDashboardView/trainerScheduleHelpers";
import { labelFor, statusHex } from "/imports/ui/views/TrainerDashboardView/attendanceHelpers";
import AttendanceRow from "/imports/ui/views/TrainerDashboardView/components/AttendanceRow";
import NoteCallout from "/imports/ui/views/TrainerDashboardView/components/NoteCallout";
import { dayLabel, servicePalette } from "../fullScheduleHelpers";

const NOTE_MAX = 2000;
// Rows a trainer can still re-mark; PENDING/CANCELED rows render read-only.
const MUTABLE = ["NEW", "ATTENDED", "NOSHOW", "LMC", "TBC"];

const snackTop = { anchorOrigin: { vertical: "top", horizontal: "center" } };

// Per-client note editor: current note (if any) with an edit toggle, so the
// sheet stays compact when notes aren't in play.
function ClientNoteEditor({ client, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(client.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.updateTrainerClientNote({ bookishClientId: client.bookishClientId, note: draft });
      onSaved(client.bookishClientId, res.note);
      setEditing(false);
      enqueueSnackbar("Note saved", { variant: "success", ...snackTop });
    } catch (err) {
      enqueueSnackbar(err.message || "Couldn't save the note", { variant: "error", ...snackTop });
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.75 }}>
        {client.note ? (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", flex: 1, minWidth: 0 }}>
            {client.note}
          </Typography>
        ) : null}
        <Button size="small" onClick={() => { setDraft(client.note || ""); setEditing(true); }} sx={{ flexShrink: 0, minWidth: 0 }}>
          {client.note ? "Edit note" : "Add note"}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label={`Note for ${client.name || "client"}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, NOTE_MAX))}
        disabled={saving}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.75 }}>
        <Button size="small" color="inherit" onClick={() => setEditing(false)} disabled={saving}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={save} disabled={saving || draft === (client.note || "")}>
          Save
        </Button>
      </Box>
    </Box>
  );
}

// Bottom sheet with everything a senior trainer may do to one booking:
// see the details (club booking note read-only), mark attendance, and edit
// per-client notes. Deliberately nothing else — no edit/checkout/cancel.
// BLOCKING and PENDING blocks are info-only.
function BookingSheet({ booking, onClose, onAttendanceChanged, onClientNoteSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const open = Boolean(booking);
  const readOnly = booking ? booking.type !== "ORIGINAL" : true;

  const [statuses, setStatuses] = useState({});
  const [savingId, setSavingId] = useState(null);
  const bookingId = booking?._id;

  // Reseed when a different booking opens (not on every prop identity change —
  // the parent rebuilds the booking object whenever an optimistic override
  // lands, and that must not clobber in-flight local state).
  useEffect(() => {
    if (!booking) return;
    const seed = {};
    (booking.clients || []).forEach((c) => {
      seed[c.bookishClientId] = c.status || "NEW";
    });
    setStatuses(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const handleSetAttendance = async (client, next) => {
    if (!next || next === statuses[client.bookishClientId]) return;
    const prev = statuses[client.bookishClientId];
    setStatuses((s) => ({ ...s, [client.bookishClientId]: next }));
    setSavingId(client.bookishClientId);
    try {
      await api.markTrainerAttendance({ bookishClientId: client.bookishClientId, status: next });
      // Push into the grid immediately so the block accent updates live.
      onAttendanceChanged?.({ [client.bookishClientId]: next });
      const msg = next === "NEW" ? `${client.name || "Client"} reset to New booking` : `${client.name || "Client"} marked ${labelFor(next)}`;
      enqueueSnackbar(msg, { variant: "success", ...snackTop });
    } catch (err) {
      setStatuses((s) => ({ ...s, [client.bookishClientId]: prev }));
      enqueueSnackbar(err.message || "Couldn't update attendance", { variant: "error", ...snackTop });
    } finally {
      setSavingId(null);
    }
  };

  const handleClose = () => onClose();

  if (!booking) return null;

  const timeLabel = [formatTime(booking.time), formatTime(booking.endTime)].filter(Boolean).join(" – ");
  const name = booking.type === "BLOCKING" ? booking.title || "Blocked slot" : booking.serviceName || booking.title || "Booking";
  const place = [booking.facilityName, booking.locationName].filter(Boolean).join(" · ");
  const trainers = (booking.trainerNames || []).filter(Boolean).join(", ");
  const seats = parseInt(booking.maxSeats, 10) > 1 ? `${booking.activeSeats}/${booking.maxSeats} booked` : "";
  const clients = booking.clients || [];
  const palette = servicePalette(booking.color);

  return (
    <Drawer
      className={NO_CAPTURE}
      anchor="bottom"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { borderTopLeftRadius: "1rem", borderTopRightRadius: "1rem", maxHeight: "88vh" } }}
    >
      <Box sx={{ p: 2, pb: 3, overflowY: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            bgcolor: palette.softFill,
            borderLeft: 4,
            borderColor: palette.edge,
            borderRadius: 1,
            p: 1.5
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="h6" fontWeight={700} sx={{ minWidth: 0 }}>
                {name}
              </Typography>
              {booking.type === "PENDING" && <Chip size="small" label="Pending payment" color="warning" variant="outlined" />}
              {booking.type === "BLOCKING" && <Chip size="small" label="Blocked" variant="outlined" />}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {[dayLabel(booking.date), timeLabel].filter(Boolean).join(" · ")}
            </Typography>
            {place && (
              <Typography variant="body2" color="text.secondary">
                {place}
              </Typography>
            )}
            {trainers && (
              <Typography variant="body2" color="text.secondary">
                {trainers}
              </Typography>
            )}
            {seats && (
              <Typography variant="body2" color="text.secondary">
                {seats}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {booking.bookingNote && <NoteCallout variant="booking" text={booking.bookingNote} sx={{ mt: 2 }} />}

        {clients.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Clients
            </Typography>
            {clients.map((c, i) => (
              <Box key={c.bookishClientId}>
                {i > 0 && <Divider sx={{ my: 2 }} />}
                <Box sx={{ borderLeft: 3, borderColor: statusHex(statuses[c.bookishClientId] || c.status), pl: 1.5 }}>
                  <AttendanceRow
                    client={c}
                    value={statuses[c.bookishClientId]}
                    saving={savingId === c.bookishClientId}
                    disabled={readOnly || !MUTABLE.includes(c.status)}
                    onSet={handleSetAttendance}
                  />
                  {!readOnly && MUTABLE.includes(c.status) && <ClientNoteEditor client={c} onSaved={onClientNoteSaved} />}
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Drawer>
  );
}

export default BookingSheet;
